/**
 * Detail Page Layout - Tabbed interface for prediction review
 */

import { Box, Container, Typography, Button, Alert, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from '@mui/material';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { predictionsApi } from '../../services/predictions.service';
import { useAppStore } from '../../store/appStore';
import { useNotificationStore } from '../../store/notificationStore';
import { mathExpressionKeys, type MathExpressionFile } from '../../services/mathExpressions.service';
import { LessonV1_SpineTagsPrediction, ReviewStatus } from '../../types';
import SummaryTab from './SummaryTab';
import MathTab from './MathTab';
import ReviewTab from './ReviewTab';
import TargetStandardsTab from './TargetStandardsTab';
import PredictionTab from './PredictionTab';
import DiffTab from './DiffTab';
import MetricsTab from './MetricsTab';
import LessonInfoHeader from './LessonInfoHeader';
import FeedbackModal from './FeedbackModal';
import ViewTagsModal from './ViewTagsModal';
import MathtasticIcon from '../common/MathtasticIcon';

export default function DetailPageLayout() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProduct, selectedExperiment } = useAppStore();
  const { showNotification } = useNotificationStore();

  // Tab state
  const [currentTab, setCurrentTab] = useState(2); // Default to Tags tab (now index 2)
  const [pendingTabChange, setPendingTabChange] = useState<number | null>(null);
  const [showTabChangeWarning, setShowTabChangeWarning] = useState(false);

  // Header state management
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showViewTagsModal, setShowViewTagsModal] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isGeneratingTags, setIsGeneratingTags] = useState(false);
  const [triggerTagGeneration, setTriggerTagGeneration] = useState(false);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Save trigger state for coordinating saves across tabs
  const [triggerSave, setTriggerSave] = useState(false);

  // Helper function to count selected math expressions from React Query cache
  const getMathExpressionCount = (): number => {
    if (!id) return 0;

    // Get cached math expressions data from React Query
    const cachedData = queryClient.getQueryData<MathExpressionFile>(mathExpressionKeys.effective(id));
    if (!cachedData || !cachedData.expressions) return 0;

    return cachedData.expressions.length;
  };

  // Fetch prediction data
  const { data: prediction, isLoading, error } = useQuery({
    queryKey: ['prediction', selectedProduct, selectedExperiment, id],
    queryFn: () => predictionsApi.getById(selectedProduct, selectedExperiment, id!),
    enabled: !!id && !!selectedProduct && !!selectedExperiment,
    staleTime: 0, // Always fetch fresh data for detail page
  });

  // Mutation for updating prediction
  const updateMutation = useMutation({
    mutationFn: (updatedPrediction: LessonV1_SpineTagsPrediction) =>
      predictionsApi.update(selectedProduct, selectedExperiment, id!, updatedPrediction),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['prediction', selectedProduct, selectedExperiment, id] });
      queryClient.invalidateQueries({ queryKey: ['predictions'] });

      // Show success feedback and clear dirty state
      showNotification('success', 'Changes saved successfully');
      setIsDirty(false);

      // Auto-open feedback modal when status changes FROM something else TO REJECTED
      // Only open if the previous status was different (i.e., this was a status change, not feedback save)
      if (data.review_status === ReviewStatus.REJECTED &&
          prediction && prediction.review_status !== ReviewStatus.REJECTED) {
        setShowFeedbackModal(true);
      }
    },
  });

  const handleUpdate = (updatedPrediction: LessonV1_SpineTagsPrediction) => {
    updateMutation.mutate(updatedPrediction);
  };

  const handleCancel = () => {
    if (isDirty) {
      setPendingNavigation(() => () => navigate(-1));
      setShowNavigationWarning(true);
    } else {
      navigate(-1);
    }
  };

  // Header action handlers
  const handleHeaderSave = () => {
    if (isDirty) {
      // Trigger save across ALL tabs regardless of which is currently active
      // This ensures all pending changes are saved
      setTriggerSave(true);
      setTimeout(() => setTriggerSave(false), 100);
    }
  };

  const handleHeaderFeedback = () => {
    setShowFeedbackModal(true);
  };

  const handleHeaderStatusChange = (status: ReviewStatus) => {
    if (prediction) {
      // Save status change immediately
      const updatedPrediction = { ...prediction, review_status: status };
      handleUpdate(updatedPrediction);
      // Don't set isDirty since this saves immediately
    }
  };

  const handleFeedbackSave = (feedback: string) => {
    if (prediction) {
      const updatedPrediction = { ...prediction, feedback };
      handleUpdate(updatedPrediction);
      setIsDirty(true);
    }
    setShowFeedbackModal(false);
  };

  const handleViewTags = () => {
    setShowViewTagsModal(true);
  };

  const handleGenerateTags = () => {
    // Switch to Tags tab if not already there
    if (currentTab !== 2) {
      setCurrentTab(2);
    }
    // Trigger tag generation in ReviewTab
    setTriggerTagGeneration(true);
    // Reset trigger after a short delay
    setTimeout(() => setTriggerTagGeneration(false), 100);
  };

  // Navigation warning for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  // Handle tab changes with dirty state checking
  const handleTabChange = (newTabIndex: number) => {
    if (isDirty) {
      // User has unsaved changes, show warning
      setPendingTabChange(newTabIndex);
      setShowTabChangeWarning(true);
    } else {
      // No unsaved changes, switch tabs immediately
      setCurrentTab(newTabIndex);
    }
  };

  // Handle tab change confirmation
  const handleConfirmTabChange = () => {
    if (pendingTabChange !== null) {
      setCurrentTab(pendingTabChange);
      setPendingTabChange(null);
    }
    setShowTabChangeWarning(false);
    setIsDirty(false); // Clear dirty state since user chose to discard changes
  };

  // Handle tab change cancellation
  const handleCancelTabChange = () => {
    setShowTabChangeWarning(false);
    setPendingTabChange(null);
  };

  // Handle save and then switch tabs
  const handleSaveAndSwitchTabs = () => {
    // Save current changes first
    handleHeaderSave();
    // Then switch to the new tab after a short delay to allow save to complete
    setTimeout(() => {
      if (pendingTabChange !== null) {
        setCurrentTab(pendingTabChange);
        setPendingTabChange(null);
      }
      setShowTabChangeWarning(false);
    }, 200);
  };

  if (isLoading) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: '95%' }}>
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography>Loading prediction...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !prediction) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: '95%' }}>
        <Box sx={{ py: 4 }}>
          <Alert severity="error">
            Failed to load prediction: {error?.message || 'Unknown error'}
          </Alert>
          <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 2, maxWidth: '95%' }}>
      {/* Back button - compact */}
      <Button
        onClick={handleCancel}
        size="small"
        sx={{
          mb: 1,
          py: 0.5,
          px: 1,
          fontSize: '0.875rem',
          minHeight: 'auto',
          textTransform: 'none'
        }}
      >
        ← Back to List
      </Button>

      {/* Lesson Info Header */}
      <LessonInfoHeader
        prediction={prediction}
        isDirty={isDirty}
        isSaving={updateMutation.isPending}
        onSave={handleHeaderSave}
        onCancel={handleCancel}
        onFeedback={handleHeaderFeedback}
        onStatusChange={handleHeaderStatusChange}
        onViewTags={handleViewTags}
        onGenerateTags={handleGenerateTags}
        isGeneratingTags={isGeneratingTags}
      />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => handleTabChange(newValue)}>
          <Tab label="Lesson Summary" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MathtasticIcon fontSize="small" />
                Math Expression Pool
                {getMathExpressionCount() > 0 && (
                  <Chip
                    label={getMathExpressionCount()}
                    size="small"
                    color="error"
                    sx={{
                      height: '18px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      minWidth: '18px',
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }}
                  />
                )}
              </Box>
            }
          />
          <Tab label="Tags" />
          <Tab label="Target Standards" />
          <Tab label="Predictions" />
          <Tab label="Changes" />
          <Tab label="Metrics" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{
        minHeight: currentTab === 1 ? 'auto' : '800px',
        maxHeight: currentTab === 1 ? 'none' : '800px',
        overflow: currentTab === 1 ? 'visible' : 'auto'
      }}>
        {currentTab === 0 && <SummaryTab prediction={prediction} />}
        {currentTab === 1 && (
          <MathTab
            prediction={prediction}
            onDirtyStateChange={setIsDirty}
            triggerSave={triggerSave}
          />
        )}
        {currentTab === 2 && (
          <ReviewTab
            prediction={prediction}
            product={selectedProduct}
            experiment={selectedExperiment}
            onUpdate={handleUpdate}
            onTagGenerationStateChange={setIsGeneratingTags}
            triggerTagGeneration={triggerTagGeneration}
            onDirtyStateChange={setIsDirty}
            triggerSave={triggerSave}
          />
        )}
        {currentTab === 3 && (
          <TargetStandardsTab
            prediction={prediction}
            onDirtyStateChange={setIsDirty}
            triggerSave={triggerSave}
          />
        )}
        {currentTab === 4 && <PredictionTab prediction={prediction} />}
        {currentTab === 5 && <DiffTab prediction={prediction} />}
        {currentTab === 6 && <MetricsTab prediction={prediction} />}
      </Box>

      {/* Feedback Modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSave={handleFeedbackSave}
        initialFeedback={prediction.feedback || ''}
        lessonName={prediction.lesson_name}
        reviewStatus={prediction.review_status}
        loading={updateMutation.isPending}
      />

      {/* View Tags Modal */}
      <ViewTagsModal
        open={showViewTagsModal}
        onClose={() => setShowViewTagsModal(false)}
        tagIds={prediction.spine_tag_ids}
        lessonName={prediction.lesson_name}
      />

      {/* Navigation Warning Dialog */}
      <Dialog open={showNavigationWarning} onClose={handleCancelNavigation}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNavigation}>Stay on Page</Button>
          <Button onClick={handleConfirmNavigation} color="error">
            Leave Without Saving
          </Button>
          <Button
            onClick={() => {
              handleHeaderSave();
              setTimeout(() => {
                handleConfirmNavigation();
              }, 200);
            }}
            variant="contained"
            color="primary"
          >
            Save & Leave
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tab Change Warning Dialog */}
      <Dialog open={showTabChangeWarning} onClose={handleCancelTabChange}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>
            You have unsaved changes that will be lost if you switch tabs. What would you like to do?
          </Typography>
          {pendingTabChange !== null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Switching to: {['Lesson Summary', 'Math', 'Tags', 'Target Standards', 'Predictions', 'Changes', 'Metrics'][pendingTabChange]} tab
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelTabChange}>Cancel</Button>
          <Button onClick={handleConfirmTabChange} color="error">
            Discard Changes
          </Button>
          <Button onClick={handleSaveAndSwitchTabs} variant="contained" color="primary">
            Save & Switch
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
